import React, { useState } from 'react';
import { Table, Button, Pagination, Icon, Loader, Dimmer } from 'semantic-ui-react';
import styled from 'styled-components';
import { FireInsuranceData } from '../../../services/dashboard';
import SummaryModal from './SummaryModal';
import { FormatCurrency } from '../../../util';
import { getFireInsuranceReport } from '../../../services/fire-insurance-policy';
import {
  dangerNotification,
  warningNotification,
  successNotification
} from '../../../services/notification';
import moment from 'moment';
import { ReportModal } from '../../../components/ReportModal';

// Estilos
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const TableContainer = styled.div`
  margin-top: 20px;
  overflow-x: auto;
`;

const StyledTable = styled(Table)`
  && {
    font-size: 0.85em;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
`;

interface FireInsuranceTableProps {
  tableData: FireInsuranceData[];
  tableLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalItems: number;
}

const FireInsuranceTable: React.FC<FireInsuranceTableProps> = ({
  tableData,
  tableLoading,
  currentPage,
  setCurrentPage,
  totalItems
}) => {
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportState, setReportState] = useState<number | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (_: any, { activePage }: any) => {
    setCurrentPage(Number(activePage));
  };

  const handleOpenSummaryModal = (estateId: string, estateName: string) => {
    setSelectedEstate({ id: estateId, name: estateName });
    setIsSummaryModalOpen(true);
  };

  const handleCloseSummaryModal = () => {
    setIsSummaryModalOpen(false);
  };

  const handleReportModalClose = () => {
    setIsReportModalOpen(false);
  };

  const handleExportReport = async () => {
    if (!selectedEstate) {
      return;
    }

    setIsExporting(true);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      const filters = {
        estateId: selectedEstate.id,
        start: moment(startDate).format('YYYY-MM-DD'),
        end: moment(endDate).format('YYYY-MM-DD')
      };

      const response = await getFireInsuranceReport(filters);

      if (!response) {
        dangerNotification(
          'Ops...',
          'Erro ao solicitar relatório de apólices de seguro incêndio.'
        );
      } else {
        setReportState(response);
        setIsReportModalOpen(true);
      }
    } catch (err) {
      console.log(err);
      dangerNotification(
        'Ops...',
        'Erro ao solicitar relatório de apólices de seguro incêndio.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Dimmer active={tableLoading} inverted>
        <Loader>Carregando...</Loader>
      </Dimmer>

      <TableContainer>
        <StyledTable celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell textAlign="center">
                Imobiliária / Razão Social
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Emitidas / Renovadas</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Canceladas</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Ativas</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Prêmio Emitido</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Prêmio Faturado</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Comissão Total</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Comissão Imobiliária</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Comissão Corretora</Table.HeaderCell>
              <Table.HeaderCell>Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {tableLoading ? (
              <Table.Row>
                <Table.Cell colSpan={9} textAlign="center">
                  <Loader active inline="centered" />
                </Table.Cell>
              </Table.Row>
            ) : tableData.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={9}>
                  <NoDataMessage>
                    <Icon name="search" />
                    Nenhum dado encontrado.
                  </NoDataMessage>
                </Table.Cell>
              </Table.Row>
            ) : (
              tableData.map((item, index) => (
                <Table.Row key={`${item.estateId}-${index}`}>
                  <Table.Cell>{item.estateName}</Table.Cell>
                  <Table.Cell>{item.issuedRenewedPolicies}</Table.Cell>
                  <Table.Cell>{item.canceledPolicies}</Table.Cell>
                  <Table.Cell>{item.activePolicies}</Table.Cell>
                  <Table.Cell>{FormatCurrency(item.totalPremium)}</Table.Cell>
                  <Table.Cell>{FormatCurrency(item.totalInvoicedPremium)}</Table.Cell>
                  <Table.Cell>{FormatCurrency(item.totalComissionPremium)}</Table.Cell>
                  <Table.Cell>{FormatCurrency(item.estateComissionPremium)}</Table.Cell>
                  <Table.Cell>{FormatCurrency(item.brokerComissionPremium)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      icon="chart bar"
                      style={{ backgroundColor: '#59249b', color: '#fff' }}
                      onClick={() =>
                        handleOpenSummaryModal(item.estateId, item.estateName)
                      }
                    ></Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </StyledTable>
      </TableContainer>

      {!tableLoading && totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            activePage={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
          />
        </PaginationContainer>
      )}

      {selectedEstate && (
        <SummaryModal
          isOpen={isSummaryModalOpen}
          onClose={handleCloseSummaryModal}
          title={`Resumo de Seguro Incêndio - ${selectedEstate.name}`}
          productType="fire_insurance"
          estateId={selectedEstate.id}
          onExport={handleExportReport}
        />
      )}

      {isReportModalOpen && (
        <ReportModal
          isOpen={isReportModalOpen}
          header="Relatório de Seguro Incêndio"
          message={
            reportState === 200
              ? 'O relatório será gerado em background e será enviado o link para download via e-mail. Atenção, só será possível gerar um novo relatório após a finalização desse.'
              : 'Já existe um relatório semelhante na fila para ser gerado, aguarde a notificação via email.'
          }
          onClose={handleReportModalClose}
        />
      )}
    </>
  );
};

export default FireInsuranceTable;
